import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Check, X, MapPin, CalendarDays, Users,
  ExternalLink, CalendarPlus, Download, Heart,
  ChevronDown, ChevronUp, Phone, ArrowRight,
  CreditCard, RefreshCw, Image, Share2
} from 'lucide-react';
import { formatDateWithDay } from '../utils/dateUtils';
import { useCompanyInfo } from '../hooks/useCompanyInfo';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

// ── Couleurs par type d'événement ────────────────────────────────────────
const EVENT_STYLES = {
  mariage:              { emoji: '💍', color: '#5DBCBF', bg: 'linear-gradient(135deg, #5DBCBF 0%, #0B4B4E 100%)' },
  anniversaire_enfant:  { emoji: '🎂', color: '#F4A7B2', bg: 'linear-gradient(135deg, #F4A7B2 0%, #C07080 100%)' },
  anniversaire_adulte:  { emoji: '🥂', color: '#D4AF37', bg: 'linear-gradient(135deg, #D4AF37 0%, #7A5E00 100%)' },
  entreprise:           { emoji: '💼', color: '#7DB1FF', bg: 'linear-gradient(135deg, #7DB1FF 0%, #1A4080 100%)' },
  soiree:               { emoji: '🎉', color: '#8B5CF6', bg: 'linear-gradient(135deg, #8B5CF6 0%, #3B1080 100%)' },
  bapteme:              { emoji: '🕊️', color: '#5DBCBF', bg: 'linear-gradient(135deg, #5DBCBF 0%, #0B4B4E 100%)' },
  garden_party:         { emoji: '🌿', color: '#1F9D7A', bg: 'linear-gradient(135deg, #1F9D7A 0%, #0B4B2E 100%)' },
  autre:                { emoji: '✨', color: '#D4AF37', bg: 'linear-gradient(135deg, #D4AF37 0%, #7A5E00 100%)' },
};
const getStyle = (type) => EVENT_STYLES[type] || EVENT_STYLES.autre;

// ── Google Calendar URL ─────────────────────────────────────────
function buildGoogleCalendarUrl(event) {
  if (!event?.date_evenement) return null;
  try {
    const start = new Date(event.date_evenement);
    const end = new Date(start.getTime() + 4 * 3600000);
    const fmt = d => d.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
    const params = new URLSearchParams({
      action: 'TEMPLATE', text: event.title || 'Événement',
      dates: `${fmt(start)}/${fmt(end)}`,
      details: event.description || '',
      location: event.lieu || '',
    });
    return `https://calendar.google.com/calendar/render?${params}`;
  } catch { return null; }
}

function buildICS(event) {
  if (!event?.date_evenement) return null;
  try {
    const start = new Date(event.date_evenement);
    const end = new Date(start.getTime() + 4 * 3600000);
    const fmt = d => d.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
    const ics = [
      'BEGIN:VCALENDAR', 'VERSION:2.0', 'PRODID:-//Show Événement//FR',
      'BEGIN:VEVENT',
      `UID:${Math.random().toString(36).slice(2)}@show-evenement.fr`,
      `DTSTART:${fmt(start)}`, `DTEND:${fmt(end)}`,
      `SUMMARY:${event.title || 'Événement'}`,
      `DESCRIPTION:${(event.description || '').replace(/\n/g, '\\n')}`,
      `LOCATION:${event.lieu || ''}`,
      'END:VEVENT', 'END:VCALENDAR',
    ].join('\r\n');
    return URL.createObjectURL(new Blob([ics], { type: 'text/calendar' }));
  } catch { return null; }
}

// ── Helpers ──────────────────────────────────────────────────
const dateLabel = (iso) => {
  if (!iso) return 'Date à confirmer';
  try { return formatDateWithDay(iso); }
  catch { return iso; }
};

export default function InvitePage() {
  const company = useCompanyInfo();
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  // RSVP
  const [rsvpStep, setRsvpStep] = useState('view'); // view | form | done
  const [rsvpResponse, setRsvpResponse] = useState('');
  const [rsvpForm, setRsvpForm] = useState({ nom_complet: '', message: '', nb_personnes: 1 });
  const [rsvpSubmitting, setRsvpSubmitting] = useState(false);
  // Cagnotte
  const [showContribute, setShowContribute] = useState(false);
  const [contributeForm, setContributeForm] = useState({ montant: 20, nom_contributeur: '', message: '', anonyme: false });
  const [contributingLoading, setContributingLoading] = useState(false);
  // UI
  const [expandSection, setExpandSection] = useState({ programme: true, infos: false, photos: true, cagnotte: true, partners: false });

  const fetchData = useCallback(async () => {
    try {
      // Tracking ouverture invitation
      fetch(`${BACKEND}/api/tracking/invitation-opened/${token}`, { method: 'POST' }).catch((err) => { /* eslint-disable-line no-empty */ });
      // Essai avec l'endpoint enrichi /full
      const r = await fetch(`${BACKEND}/api/invite/${token}/full`);
      if (r.ok) {
        const d = await r.json();
        setData(d);
        // Si déjà répondu
        if (d.guest?.statut === 'confirme' || d.guest?.statut === 'refuse') {
          setRsvpStep('done');
          setRsvpResponse(d.guest.statut);
        }
      } else {
        // Fallback endpoint standard
        const r2 = await fetch(`${BACKEND}/api/invite/${token}`);
        if (!r2.ok) throw new Error('not found');
        const d2 = await r2.json();
        setData({ ...d2, photos: [], cagnotte: null, partners: [] });
        if (d2.guest?.statut === 'confirme' || d2.guest?.statut === 'refuse') {
          setRsvpStep('done');
          setRsvpResponse(d2.guest.statut);
        }
      }
    } catch {
      setData(null);
    }
    setLoading(false);
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  async function submitRSVP() {
    setRsvpSubmitting(true);
    try {
      const res = await fetch(`${BACKEND}/api/invite/${token}/rsvp`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ response: rsvpResponse, ...rsvpForm }),
      });
      if (res.ok) {
        setRsvpStep('done');
        toast.success(rsvpResponse === 'confirme' ? '🎉 Présence confirmée !' : 'Réponse enregistrée');
      } else { toast.error('Erreur, réessayez'); }
    } catch { toast.error('Erreur de connexion'); }
    setRsvpSubmitting(false);
  }

  async function contribute() {
    if (!data?.event) return;
    setContributingLoading(true);
    try {
      const r = await fetch(`${BACKEND}/api/events/${data.event.event_id}/cagnotte/contribute`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(contributeForm),
      });
      const d = await r.json();
      if (d.checkout_url) { window.location.href = d.checkout_url; }
      else if (d.simulated) {
        toast.success('Participation enregistrée !');
        setShowContribute(false);
        fetchData();
      }
    } catch { toast.error('Erreur paiement'); }
    setContributingLoading(false);
  }

  const toggleSection = (key) => setExpandSection(p => ({ ...p, [key]: !p[key] }));

  // ── Loading ──
  if (loading) return (
    <div style={{ minHeight: '100vh', background: '#FEFAF5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{ width: 44, height: 44, borderRadius: '50%', border: '3px solid #E9DDB7', borderTopColor: '#5DBCBF', animation: 'spin 0.8s linear infinite', margin: '0 auto 16px' }} />
        <p style={{ color: 'rgba(26,26,46,0.5)', fontSize: 13 }}>Chargement de votre invitation…</p>
      </div>
    </div>
  );

  // ── Introuvable ──
  if (!data) return (
    <div style={{ minHeight: '100vh', background: '#FEFAF5', fontFamily: 'Manrope, sans-serif' }}>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 24, minHeight: '60vh' }}>
        <div style={{ fontSize: 52, marginBottom: 16 }}>🔍</div>
        <h1 style={{ color: '#1A1A2E', fontSize: 20, fontWeight: 700, margin: '0 0 8px' }}>Invitation introuvable</h1>
        <p style={{ color: 'rgba(26,26,46,0.5)', fontSize: 13, textAlign: 'center', maxWidth: 300, margin: '0 0 24px' }}>
          Ce lien n'existe pas ou a expiré. Contactez l'organisateur.
        </p>
        <a href="/" style={{ color: '#5DBCBF', textDecoration: 'none', fontSize: 13 }}>Retour à l'accueil →</a>
      </div>
      
      {/* ── BLOC ACQUISITION (Phase 16) même si invitation introuvable ── */}
      <div style={{ padding: '0 20px 60px', maxWidth: 600, margin: '0 auto' }}>
        <div style={{ margin: '20px 0 0', borderRadius: 16, overflow: 'hidden', border: '1px solid rgba(93,188,191,0.3)' }}>
          <div style={{ background: 'linear-gradient(135deg, rgba(93,188,191,0.15), rgba(212,175,55,0.08))', padding: '20px 22px', textAlign: 'center' }}>
            <div style={{ fontSize: 28, marginBottom: 8 }}>🎉</div>
            <p style={{ fontWeight: 800, fontSize: 16, color: '#1A1A2E', margin: '0 0 6px' }}>
              Organisez votre propre événement
            </p>
            <p style={{ fontSize: 13, color: 'rgba(26,26,46,0.6)', margin: '0 0 16px', lineHeight: 1.6 }}>
              Créez votre espace événement gratuit — invitations, cagnotte, IA, photos, partenaires.
            </p>
            <a href="/mes-evenements"
              data-testid="invite-cta-create-event"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 7, padding: '13px 24px', borderRadius: 13, background: 'linear-gradient(135deg, #5DBCBF, #0B4B4E)', color: '#FFFFFF', fontWeight: 700, fontSize: 14, textDecoration: 'none', boxShadow: '0 6px 20px rgba(93,188,191,0.3)' }}>
              Créer mon événement →
            </a>
            <p style={{ fontSize: 11, color: 'rgba(26,26,46,0.35)', margin: '10px 0 0' }}>
              Gratuit · Sans engagement · Résultat en 2 minutes
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  const { guest, event, photos = [], cagnotte, partners = [] } = data;
  const style = getStyle(event.event_type);
  const guestName = [guest.prenom, guest.nom].filter(Boolean).join(' ') || 'vous';
  const alreadyResponded = rsvpStep === 'done';

  return (
    <div style={{ minHeight: '100vh', background: '#FEFAF5', fontFamily: 'Manrope, Arial, sans-serif', maxWidth: 600, margin: '0 auto' }}>

      {/* ── BLOC 1 : HERO ÉVÉNEMENT ── */}
      <div style={{
        background: style.bg,
        padding: '48px 28px 44px',
        textAlign: 'center',
        position: 'relative',
        overflow: 'hidden',
      }}>
        {/* Cercles décoratifs */}
        <div style={{ position: 'absolute', top: -40, right: -40, width: 200, height: 200, borderRadius: '50%', background: 'rgba(255,255,255,0.06)' }} />
        <div style={{ position: 'absolute', bottom: -60, left: -30, width: 160, height: 160, borderRadius: '50%', background: 'rgba(255,255,255,0.05)' }} />

        {/* Badges officiel + privé */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginBottom: 16, position: 'relative', zIndex: 2 }}>
          <span style={{ background: 'rgba(255,255,255,0.2)', backdropFilter: 'blur(8px)', border: '1px solid rgba(255,255,255,0.3)', color: '#FFFFFF', fontSize: 11, fontWeight: 700, padding: '4px 12px', borderRadius: 20, letterSpacing: '0.06em' }}>
            🎫 Invitation officielle
          </span>
          <span style={{ background: 'rgba(0,0,0,0.2)', border: '1px solid rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.85)', fontSize: 11, fontWeight: 600, padding: '4px 12px', borderRadius: 20 }}>
            🔒 Événement privé
          </span>
        </div>

        <div style={{ fontSize: 56, marginBottom: 12, position: 'relative' }}>{style.emoji}</div>
        <p style={{ color: 'rgba(255,255,255,0.8)', fontSize: 12, letterSpacing: '0.1em', textTransform: 'uppercase', margin: '0 0 6px', fontWeight: 600 }}>
          Invitation personnelle
        </p>
        <h1 style={{ color: '#FFFFFF', fontSize: 26, fontWeight: 800, margin: '0 0 6px', letterSpacing: '-0.02em', lineHeight: 1.2 }}>
          {event.title}
        </h1>
        <p style={{ color: 'rgba(255,255,255,0.75)', fontSize: 14, margin: '0 0 20px' }}>{event.event_type_label}</p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'center' }}>
          {event.date_evenement && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'rgba(255,255,255,0.9)', fontSize: 14, fontWeight: 600 }}>
              <CalendarDays size={15} /> {dateLabel(event.date_evenement)}
            </div>
          )}
          {event.lieu && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'rgba(255,255,255,0.8)', fontSize: 13 }}>
              <MapPin size={14} /> {event.lieu}
            </div>
          )}
        </div>
        {/* Bouton partager */}
        <button
          onClick={() => {
            const url = window.location.href;
            if (navigator.share) {
              navigator.share({ title: event.title, text: `Vous êtes invité(e) à ${event.title}`, url })
                .catch(() => { navigator.clipboard?.writeText(url); });
            } else {
              navigator.clipboard?.writeText(url);
              alert('Lien copié !');
            }
          }}
          style={{ marginTop: 16, display: 'flex', alignItems: 'center', gap: 6, padding: '8px 18px', borderRadius: 20, background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.25)', color: '#FFFFFF', fontSize: 12, fontWeight: 600, cursor: 'pointer', backdropFilter: 'blur(8px)', position: 'relative', zIndex: 2 }}
        >
          <Share2 size={13} /> Partager l'événement
        </button>
      </div>

      {/* ── STICKY MOBILE — Répondre / Participer ── */}
      {!alreadyResponded && (
        <div style={{ position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 9990, padding: '12px 16px', background: 'rgba(254,250,245,0.97)', backdropFilter: 'blur(12px)', borderTop: '1px solid #E9DDB7', boxShadow: '0 -4px 20px rgba(26,26,46,0.08)', display: 'flex', gap: 10, maxWidth: 600, margin: '0 auto' }}>
          <button
            data-testid="sticky-rsvp-confirm"
            onClick={() => { setRsvpResponse('confirme'); setRsvpStep('form'); document.getElementById('bloc-rsvp')?.scrollIntoView({ behavior: 'smooth' }); }}
            style={{ flex: 2, padding: '13px', borderRadius: 13, background: style.bg, color: '#FFFFFF', fontWeight: 700, fontSize: 14, border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
            <Check size={16} /> Je serai là
          </button>
          {cagnotte && (
            <button
              data-testid="sticky-participate"
              onClick={() => { setShowContribute(true); document.getElementById('bloc-cagnotte')?.scrollIntoView({ behavior: 'smooth' }); }}
              style={{ flex: 1, padding: '13px', borderRadius: 13, background: '#FFFFFF', border: '1.5px solid rgba(212,175,55,0.5)', color: '#C58B19', fontWeight: 700, fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5 }}>
              <CreditCard size={15} /> Participer
            </button>
          )}
        </div>
      )}

      {/* Contenu principal */}
      <div style={{ padding: `0 20px ${!alreadyResponded ? '100px' : '60px'}` }}>

        {/* ── BLOC 2 : MESSAGE ORGANISATEUR ── */}
        <div id="bloc-rsvp" style={{ background: '#FFFFFF', borderRadius: 16, padding: '20px 22px', margin: '20px 0 0', boxShadow: '0 2px 16px rgba(26,26,46,0.06)', border: '1px solid #E9DDB7' }}>
          <p style={{ color: '#1A1A2E', fontSize: 15, fontWeight: 600, margin: '0 0 6px' }}>
            Bonjour {guestName} {guest.role === 'temoin' ? '— Témoin ⭐' : ''} 👋
          </p>
          <p style={{ color: 'rgba(26,26,46,0.65)', fontSize: 14, lineHeight: 1.7, margin: 0 }}>
            {event.owner_name ? `${event.owner_name} a le plaisir` : 'Vous êtes invité(e)'} de vous convier à cet événement spécial.
            {event.description && ` ${event.description.slice(0, 120)}${event.description.length > 120 ? '…' : ''}`}
            <br/>
            <em style={{ color: 'rgba(26,26,46,0.5)' }}>Nous avons hâte de partager ce moment avec vous.</em>
          </p>
        </div>

        {/* ── BLOC 3 : RSVP ── */}
        <div style={{ background: '#FFFFFF', borderRadius: 16, padding: '20px 22px', margin: '12px 0 0', border: '1px solid #E9DDB7' }}>
          <p style={{ fontSize: 13, fontWeight: 700, color: 'rgba(26,26,46,0.5)', textTransform: 'uppercase', letterSpacing: '0.08em', margin: '0 0 14px' }}>Réponse</p>

          {!alreadyResponded && rsvpStep === 'view' && (
            <>
              <p style={{ fontSize: 15, fontWeight: 600, color: '#1A1A2E', margin: '0 0 14px' }}>Serez-vous présent(e) ?</p>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <button data-testid="rsvp-confirm-btn"
                  onClick={() => { setRsvpResponse('confirme'); setRsvpStep('form'); }}
                  style={{
                    padding: '14px 10px', borderRadius: 14, background: style.bg,
                    color: '#FFFFFF', fontWeight: 700, fontSize: 15, border: 'none', cursor: 'pointer',
                    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 5,
                  }}>
                  <Check size={22} /> J'y serai !
                </button>
                <button data-testid="rsvp-decline-btn"
                  onClick={() => { setRsvpResponse('refuse'); setRsvpStep('form'); }}
                  style={{
                    padding: '14px 10px', borderRadius: 14, background: '#FFFFFF',
                    color: '#D94B5D', fontWeight: 600, fontSize: 15, border: '1.5px solid rgba(217,75,93,0.3)', cursor: 'pointer',
                    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 5,
                  }}>
                  <X size={22} /> Désolé(e)
                </button>
              </div>
            </>
          )}

          {!alreadyResponded && rsvpStep === 'form' && (
            <div>
              <p style={{ fontWeight: 700, fontSize: 14, color: rsvpResponse === 'confirme' ? '#1F9D7A' : '#D94B5D', margin: '0 0 14px' }}>
                {rsvpResponse === 'confirme' ? '✅ Vous confirmez votre présence' : '😔 Vous ne pourrez pas venir'}
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 14 }}>
                <input type="text" placeholder="Votre nom complet"
                  value={rsvpForm.nom_complet} onChange={e => setRsvpForm(p => ({ ...p, nom_complet: e.target.value }))}
                  style={{ padding: '10px 14px', borderRadius: 10, border: '1px solid #E9DDB7', fontSize: 13, color: '#1A1A2E', background: '#FEFAF5' }} />
                {rsvpResponse === 'confirme' && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <label style={{ fontSize: 12, color: 'rgba(26,26,46,0.6)' }}>Nb personnes :</label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <button onClick={() => setRsvpForm(p => ({ ...p, nb_personnes: Math.max(1, p.nb_personnes - 1) }))}
                        style={{ width: 32, height: 32, borderRadius: 8, border: '1px solid #E9DDB7', background: '#FEFAF5', cursor: 'pointer', fontSize: 16 }}>−</button>
                      <span style={{ fontWeight: 700, fontSize: 16, color: '#1A1A2E', minWidth: 20, textAlign: 'center' }}>{rsvpForm.nb_personnes}</span>
                      <button onClick={() => setRsvpForm(p => ({ ...p, nb_personnes: p.nb_personnes + 1 }))}
                        style={{ width: 32, height: 32, borderRadius: 8, border: '1px solid #E9DDB7', background: '#FEFAF5', cursor: 'pointer', fontSize: 16 }}>+</button>
                    </div>
                  </div>
                )}
                <textarea rows={2} placeholder="Un message (facultatif)..."
                  value={rsvpForm.message} onChange={e => setRsvpForm(p => ({ ...p, message: e.target.value }))}
                  style={{ padding: '10px 14px', borderRadius: 10, border: '1px solid #E9DDB7', fontSize: 13, color: '#1A1A2E', background: '#FEFAF5', resize: 'none' }} />
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={() => setRsvpStep('view')}
                  style={{ flex: 1, padding: '11px', borderRadius: 11, background: '#FEFAF5', border: '1px solid #E9DDB7', color: 'rgba(26,26,46,0.6)', fontSize: 13, cursor: 'pointer' }}>
                  Retour
                </button>
                <button data-testid="rsvp-submit-btn"
                  onClick={submitRSVP} disabled={rsvpSubmitting}
                  style={{
                    flex: 2, padding: '11px', borderRadius: 11, fontSize: 14, fontWeight: 700, border: 'none', cursor: 'pointer',
                    background: rsvpResponse === 'confirme' ? style.bg : '#D94B5D',
                    color: '#FFFFFF', opacity: rsvpSubmitting ? 0.6 : 1,
                  }}>
                  {rsvpSubmitting ? 'Envoi…' : 'Envoyer ma réponse'}
                </button>
              </div>
            </div>
          )}

          {alreadyResponded && (
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 36, marginBottom: 10 }}>
                {rsvpResponse === 'confirme' ? '🎉' : '😔'}
              </div>
              <p style={{ fontWeight: 800, fontSize: 17, color: '#1A1A2E', margin: '0 0 6px' }}>
                {rsvpResponse === 'confirme' ? 'Votre présence est confirmée !' : 'Votre absence est enregistrée.'}
              </p>
              <p style={{ color: 'rgba(26,26,46,0.55)', fontSize: 13, margin: '0 0 16px', lineHeight: 1.6 }}>
                {rsvpResponse === 'confirme'
                  ? `L'organisateur a été notifié. À très bientôt à ${event.lieu || "l'événement"} !`
                  : 'Merci pour votre réponse. L\'organisateur a été notifié.'}
              </p>
              {rsvpResponse === 'confirme' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {buildGoogleCalendarUrl(event) && (
                    <a href={buildGoogleCalendarUrl(event)} target="_blank" rel="noreferrer"
                      style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7, padding: '12px', borderRadius: 12, background: '#FFFFFF', border: '1.5px solid rgba(93,188,191,0.35)', color: '#0B4B4E', fontSize: 13, fontWeight: 600, textDecoration: 'none' }}>
                      <CalendarPlus size={16} style={{ color: '#5DBCBF' }} /> Ajouter à Google Agenda
                    </a>
                  )}
                  <a href={buildICS(event) || '#'} download={`${event.title || 'evenement'}.ics`}
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7, padding: '12px', borderRadius: 12, background: '#FFFFFF', border: '1.5px solid rgba(26,26,46,0.12)', color: '#1A1A2E', fontSize: 13, fontWeight: 600, textDecoration: 'none' }}>
                    <CalendarDays size={16} /> Apple Agenda / Outlook (.ics)
                  </a>
                </div>
              )}
            </div>
          )}
        </div>

        {/* ── BLOC 4 : PROGRAMME ── */}
        {event.programme?.length > 0 && (
          <div style={{ background: '#FFFFFF', borderRadius: 16, margin: '12px 0 0', border: '1px solid #E9DDB7', overflow: 'hidden' }}>
            <button onClick={() => toggleSection('programme')}
              style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 22px', background: 'none', border: 'none', cursor: 'pointer' }}>
              <p style={{ fontSize: 13, fontWeight: 700, color: 'rgba(26,26,46,0.5)', textTransform: 'uppercase', letterSpacing: '0.08em', margin: 0 }}>
                📋 Programme
              </p>
              {expandSection.programme ? <ChevronUp size={16} style={{ color: 'rgba(26,26,46,0.4)' }} /> : <ChevronDown size={16} style={{ color: 'rgba(26,26,46,0.4)' }} />}
            </button>
            {expandSection.programme && (
              <div style={{ padding: '0 22px 18px', borderTop: '1px solid #F0E8CC' }}>
                {event.programme.map((step, i) => (
                  <div key={step.heure ? `prog-${step.heure}` : `prog-${i}`} style={{ display: 'flex', gap: 14, paddingTop: 12 }}>
                    <div style={{ minWidth: 48, textAlign: 'right' }}>
                      <span style={{ fontSize: 13, fontWeight: 700, color: style.color }}>{step.heure}</span>
                    </div>
                    <div style={{ flex: 1 }}>
                      <p style={{ fontSize: 13, color: '#1A1A2E', margin: 0, lineHeight: 1.5 }}>{step.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── BLOC 5 : INFOS PRATIQUES + DRESS CODE ── */}
        {(event.infos_pratiques || (event.dress_code && Object.keys(event.dress_code).length > 0)) && (
          <div style={{ background: '#FFFFFF', borderRadius: 16, margin: '12px 0 0', border: '1px solid #E9DDB7', overflow: 'hidden' }}>
            <button onClick={() => toggleSection('infos')}
              style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 22px', background: 'none', border: 'none', cursor: 'pointer' }}>
              <p style={{ fontSize: 13, fontWeight: 700, color: 'rgba(26,26,46,0.5)', textTransform: 'uppercase', letterSpacing: '0.08em', margin: 0 }}>
                ℹ️ Informations
              </p>
              {expandSection.infos ? <ChevronUp size={16} style={{ color: 'rgba(26,26,46,0.4)' }} /> : <ChevronDown size={16} style={{ color: 'rgba(26,26,46,0.4)' }} />}
            </button>
            {expandSection.infos && (
              <div style={{ padding: '0 22px 18px', borderTop: '1px solid #F0E8CC', display: 'flex', flexDirection: 'column', gap: 12 }}>
                {event.infos_pratiques && (
                  <div style={{ paddingTop: 12 }}>
                    <p style={{ fontSize: 11, fontWeight: 700, color: style.color, marginBottom: 4 }}>Accès & informations</p>
                    <p style={{ fontSize: 13, color: 'rgba(26,26,46,0.7)', lineHeight: 1.6, margin: 0 }}>{event.infos_pratiques}</p>
                  </div>
                )}
                {event.dress_code?.style && (
                  <div style={{ padding: '12px', borderRadius: 10, background: 'rgba(212,175,55,0.08)', border: '1px solid rgba(212,175,55,0.2)' }}>
                    <p style={{ fontSize: 11, fontWeight: 700, color: '#7A5E00', margin: '0 0 4px' }}>👔 Dress code</p>
                    <p style={{ fontSize: 14, fontWeight: 600, color: '#7A5E00', margin: '0 0 2px' }}>{event.dress_code.style}</p>
                    {event.dress_code.couleurs && <p style={{ fontSize: 12, color: 'rgba(122,94,0,0.7)', margin: 0 }}>Couleurs : {event.dress_code.couleurs}</p>}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* ── BLOC 6 : GALERIE PHOTOS ── */}
        {photos.length > 0 && (
          <div style={{ background: '#FFFFFF', borderRadius: 16, margin: '12px 0 0', border: '1px solid #E9DDB7', overflow: 'hidden' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 22px' }}>
              <p style={{ fontSize: 13, fontWeight: 700, color: 'rgba(26,26,46,0.5)', textTransform: 'uppercase', letterSpacing: '0.08em', margin: 0 }}>
                📸 Photos ({photos.length})
              </p>
              <div style={{ display: 'flex', gap: 6 }}>
                <button
                  onClick={() => {
                    const links = photos.map(p => p.url);
                    navigator.clipboard?.writeText(links.join('\n'));
                    toast.success('Liens copiés !');
                  }}
                  style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '5px 10px', borderRadius: 8, background: 'var(--teal-mist, #E7F6F6)', border: 'none', color: '#0B4B4E', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>
                  <Share2 size={12} /> Partager
                </button>
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 3, padding: '0 3px 12px' }}>
              {photos.slice(0, 9).map((ph, i) => (
                <div key={ph.photo_id || i} style={{ position: 'relative', aspectRatio: '1', borderRadius: 8, overflow: 'hidden' }}>
                  <img src={ph.url} alt={ph.caption || `Photo ${i + 1}`}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }} loading="lazy" />
                  <a href={ph.url} download target="_blank" rel="noreferrer"
                    style={{
                      position: 'absolute', bottom: 4, right: 4,
                      width: 26, height: 26, borderRadius: 7,
                      background: 'rgba(255,255,255,0.9)', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                    <Download size={13} style={{ color: '#1A1A2E' }} />
                  </a>
                </div>
              ))}
            </div>
            {photos.length > 9 && (
              <p style={{ textAlign: 'center', fontSize: 12, color: style.color, fontWeight: 600, margin: '0 0 14px' }}>
                +{photos.length - 9} photos disponibles
              </p>
            )}
          </div>
        )}

        {/* ── BLOC 7 : CAGNOTTE ── */}
        {cagnotte && (
          <div id="bloc-cagnotte" style={{ borderRadius: 16, margin: '12px 0 0', overflow: 'hidden', border: '1.5px solid rgba(212,175,55,0.3)' }}>
            <div style={{ background: 'linear-gradient(135deg, rgba(212,175,55,0.15), rgba(93,188,191,0.1))', padding: '16px 22px', borderBottom: '1px solid rgba(212,175,55,0.2)' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <p style={{ fontSize: 13, fontWeight: 700, color: 'rgba(26,26,46,0.5)', textTransform: 'uppercase', letterSpacing: '0.08em', margin: '0 0 2px' }}>
                    💰 Cagnotte
                  </p>
                  <p style={{ fontSize: 15, fontWeight: 700, color: '#1A1A2E', margin: 0 }}>{cagnotte.titre}</p>
                </div>
                {cagnotte.objectif && (
                  <div style={{ textAlign: 'right' }}>
                    <p style={{ fontSize: 18, fontWeight: 800, color: '#D4AF37', margin: 0 }}>{(cagnotte.total_collecte || 0).toFixed(0)}€</p>
                    <p style={{ fontSize: 11, color: 'rgba(26,26,46,0.5)', margin: 0 }}>sur {cagnotte.objectif}€</p>
                  </div>
                )}
              </div>
              {cagnotte.objectif && (
                <div style={{ marginTop: 10 }}>
                  <div style={{ width: '100%', height: 8, borderRadius: 4, background: 'rgba(212,175,55,0.2)', overflow: 'hidden' }}>
                    <div style={{ height: '100%', borderRadius: 4, background: 'linear-gradient(90deg, #D4AF37, #C58B19)', width: `${cagnotte.pct_objectif || 0}%`, transition: 'width 0.5s' }} />
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
                    <span style={{ fontSize: 11, color: 'rgba(26,26,46,0.5)' }}>{cagnotte.nb_contributeurs || 0} participant(s)</span>
                    <span style={{ fontSize: 11, fontWeight: 700, color: '#D4AF37' }}>{cagnotte.pct_objectif || 0}%</span>
                  </div>
                  {cagnotte.objectif && (
                    <p style={{ fontSize: 11, color: 'rgba(26,26,46,0.5)', margin: '4px 0 0', textAlign: 'right' }}>
                      Reste {Math.max(0, cagnotte.objectif - (cagnotte.total_collecte || 0)).toFixed(0)}€ à collecter
                    </p>
                  )}
                  {/* Message dynamique conversion */}
                  {cagnotte.objectif && (cagnotte.pct_objectif || 0) < 100 && (
                    <div style={{ marginTop: 10, padding: '8px 12px', borderRadius: 10, background: 'rgba(212,175,55,0.1)', border: '1px solid rgba(212,175,55,0.2)', textAlign: 'center' }}>
                      <p style={{ fontSize: 12, fontWeight: 700, color: '#7A5E00', margin: 0 }}>
                        🎯 Plus que {Math.max(0, cagnotte.objectif - (cagnotte.total_collecte || 0)).toFixed(0)}€ pour atteindre l'objectif !
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div style={{ background: '#FFFFFF', padding: '14px 22px' }}>
              {!showContribute ? (
                <button data-testid="participate-cagnotte-btn"
                  onClick={() => setShowContribute(true)}
                  style={{ width: '100%', padding: '14px', borderRadius: 12, background: 'linear-gradient(135deg, #5DBCBF, #0B4B4E)', color: '#FFFFFF', fontWeight: 700, fontSize: 14, border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7 }}>
                  <CreditCard size={16} /> Participer à l'événement
                </button>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <p style={{ fontSize: 13, fontWeight: 600, color: '#1A1A2E', margin: 0 }}>Choisir un montant</p>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {[10, 20, 50, 100].map(m => (
                      <button key={m} onClick={() => setContributeForm(p => ({ ...p, montant: m }))}
                        style={{ flex: 1, minWidth: 50, padding: '10px 6px', borderRadius: 10, fontWeight: 700, fontSize: 14, cursor: 'pointer', border: `1.5px solid ${contributeForm.montant === m ? '#5DBCBF' : '#E9DDB7'}`, background: contributeForm.montant === m ? 'rgba(93,188,191,0.1)' : '#FEFAF5', color: contributeForm.montant === m ? '#0B4B4E' : '#1A1A2E' }}>
                        {m}€
                      </button>
                    ))}
                  </div>
                  <input type="text" placeholder="Votre nom (facultatif)"
                    value={contributeForm.nom_contributeur}
                    onChange={e => setContributeForm(p => ({ ...p, nom_contributeur: e.target.value }))}
                    style={{ padding: '10px 14px', borderRadius: 10, border: '1px solid #E9DDB7', fontSize: 13, color: '#1A1A2E', background: '#FEFAF5' }} />
                  <input type="text" placeholder="Un message (facultatif)"
                    value={contributeForm.message}
                    onChange={e => setContributeForm(p => ({ ...p, message: e.target.value }))}
                    style={{ padding: '10px 14px', borderRadius: 10, border: '1px solid #E9DDB7', fontSize: 13, color: '#1A1A2E', background: '#FEFAF5' }} />
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: 'rgba(26,26,46,0.6)', cursor: 'pointer' }}>
                    <input type="checkbox" checked={contributeForm.anonyme} onChange={e => setContributeForm(p => ({ ...p, anonyme: e.target.checked }))} />
                    Rester anonyme
                  </label>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button onClick={() => setShowContribute(false)}
                      style={{ flex: 1, padding: '11px', borderRadius: 11, background: '#FEFAF5', border: '1px solid #E9DDB7', color: 'rgba(26,26,46,0.6)', fontSize: 13, cursor: 'pointer' }}>
                      Annuler
                    </button>
                    <button onClick={contribute} disabled={contributingLoading}
                      style={{ flex: 2, padding: '11px', borderRadius: 11, background: 'linear-gradient(135deg, #5DBCBF, #0B4B4E)', color: '#FFFFFF', fontWeight: 700, fontSize: 14, border: 'none', cursor: 'pointer', opacity: contributingLoading ? 0.6 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                      {contributingLoading ? <RefreshCw size={14} className="animate-spin" /> : <CreditCard size={14} />}
                      Payer {contributeForm.montant}€
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── BLOC 8 : PARTENAIRES ── */}
        {partners.length > 0 && (
          <div style={{ background: '#FFFFFF', borderRadius: 16, margin: '12px 0 0', border: '1px solid #E9DDB7', overflow: 'hidden' }}>
            <button onClick={() => toggleSection('partners')}
              style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 22px', background: 'none', border: 'none', cursor: 'pointer' }}>
              <p style={{ fontSize: 13, fontWeight: 700, color: 'rgba(26,26,46,0.5)', textTransform: 'uppercase', letterSpacing: '0.08em', margin: 0 }}>
                🤝 Partenaires
              </p>
              {expandSection.partners ? <ChevronUp size={16} style={{ color: 'rgba(26,26,46,0.4)' }} /> : <ChevronDown size={16} style={{ color: 'rgba(26,26,46,0.4)' }} />}
            </button>
            {expandSection.partners && (
              <div style={{ padding: '0 16px 16px', borderTop: '1px solid #F0E8CC', display: 'flex', flexDirection: 'column', gap: 8, paddingTop: 10 }}>
                {partners.map((p, i) => (
                  <div key={p.id} style={{ display: 'flex', gap: 12, padding: '12px', borderRadius: 12, background: p.featured ? 'rgba(212,175,55,0.06)' : '#FEFAF5', border: `1px solid ${p.featured ? 'rgba(212,175,55,0.2)' : '#F0E8CC'}` }}>
                    {p.logo ? (
                      <img src={p.logo} alt={p.nom} style={{ width: 44, height: 44, borderRadius: 10, objectFit: 'contain', background: '#F5F5F5', padding: 3, flexShrink: 0 }} loading="lazy" />
                    ) : p.image ? (
                      <img src={p.image} alt={p.nom} style={{ width: 44, height: 44, borderRadius: 10, objectFit: 'cover', flexShrink: 0 }} loading="lazy" />
                    ) : null}
                    <div style={{ flex: 1 }}>
                      <p style={{ fontSize: 13, fontWeight: 700, color: '#1A1A2E', margin: '0 0 2px' }}>{p.nom}</p>
                      <p style={{ fontSize: 11, color: 'rgba(26,26,46,0.55)', lineHeight: 1.4, margin: '0 0 6px' }}>{p.description?.slice(0, 80)}…</p>
                      {p.telephone && (
                        <a href={`tel:${p.telephone.replace(/\s/g, '')}`}
                          style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 10px', borderRadius: 7, background: 'rgba(93,188,191,0.1)', color: '#0B4B4E', fontSize: 11, fontWeight: 600, textDecoration: 'none' }}>
                          <Phone size={11} /> Contacter
                        </a>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── BLOC 9 : CTA FINAL ── */}
        <div style={{ margin: '20px 0 0', display: 'flex', flexDirection: 'column', gap: 10 }}>
          {event.slug && (
            <a href={`/mon-evenement/${event.slug}`}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7, padding: '14px', borderRadius: 14, background: '#FFFFFF', border: '1.5px solid #E9DDB7', color: '#1A1A2E', fontSize: 13, fontWeight: 600, textDecoration: 'none' }}>
              <ExternalLink size={15} style={{ color: '#5DBCBF' }} /> Voir tous les détails de l'événement
            </a>
          )}
        </div>

        {/* ── BLOC ACQUISITION + VIRALITÉ ENRICHI (Phase 16+) ── */}
        <div style={{ margin: '20px 0 0', borderRadius: 16, overflow: 'hidden', border: '1px solid rgba(212,175,55,0.3)' }}>
          <div style={{ background: 'linear-gradient(135deg, rgba(212,175,55,0.12), rgba(93,188,191,0.08))', padding: '20px 22px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
              <span style={{ fontSize: 24 }}>🎁</span>
              <div>
                <p style={{ fontWeight: 800, fontSize: 15, color: '#1A1A2E', margin: 0 }}>
                  Gagnez de l'argent avec cet événement
                </p>
                <p style={{ fontSize: 12, color: 'rgba(26,26,46,0.6)', margin: '3px 0 0', lineHeight: 1.5 }}>
                  Partagez vous aussi vos événements et gagnez jusqu'à 15%
                </p>
              </div>
            </div>
            {/* Partage rapide */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
              {[
                { label: 'WhatsApp', icon: '💬', bg: '#25D366', color: '#FFFFFF', url: `https://wa.me/?text=${encodeURIComponent(`🎉 ${event.title} — Je t'invite ! ${window.location.href}`)}` },
                { label: 'SMS', icon: '📱', bg: '#5DBCBF', color: '#FFFFFF', url: `sms:?body=${encodeURIComponent(`🎉 ${event.title} — Je t'invite ! ${window.location.href}`)}` },
                { label: 'Facebook', icon: '👥', bg: '#1877F2', color: '#FFFFFF', url: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}` },
              ].map(s => (
                <a key={s.label} href={s.url} target="_blank" rel="noreferrer"
                  style={{ flex: 1, minWidth: 80, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5, padding: '9px 10px', borderRadius: 10, background: s.bg, color: s.color, fontSize: 12, fontWeight: 700, textDecoration: 'none' }}>
                  <span>{s.icon}</span> {s.label}
                </a>
              ))}
            </div>
            <a href="/mes-evenements"
              data-testid="invite-cta-create-event"
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7, padding: '12px 20px', borderRadius: 12, background: 'linear-gradient(135deg, #5DBCBF, #0B4B4E)', color: '#FFFFFF', fontWeight: 700, fontSize: 13, textDecoration: 'none', boxShadow: '0 4px 16px rgba(93,188,191,0.28)' }}>
              Créer mon événement → Gagner jusqu'à 15%
            </a>
            <p style={{ fontSize: 10, color: 'rgba(26,26,46,0.3)', textAlign: 'center', margin: '8px 0 0' }}>
              Gratuit · Sans engagement · Résultat en 2 minutes
            </p>
          </div>
        </div>

        {/* Footer */}
        <div style={{ textAlign: 'center', marginTop: 20, paddingBottom: 8 }}>
          <p style={{ fontSize: 11, color: 'rgba(26,26,46,0.3)', margin: 0 }}>
            Invitation créée avec{' '}
            <a href="/" style={{ color: '#5DBCBF', textDecoration: 'none', fontWeight: 600 }}>Show Événement</a>
            {' '}— {company.adresse_encaissement}
          </p>
        </div>
      </div>
    </div>
  );
}
